/**
 * `window.__kn`: a small read-only view of the engine for e2e tests
 * (test strategy "E2E hooks"). Present in dev builds, and in production only
 * with `?debug`, so a normal visitor never sees it.
 */
import { CommandsModule } from "../commands/CommandsModule";
import { CoreModule } from "../core/CoreModule";
import type { Engine } from "../core/Engine";
import { aabbCenter } from "../core/math/aabb";
import { mat4GetTranslation } from "../core/math/mat4";
import { nextFrame } from "../core/nextFrame";
import type { Vec3 } from "../core/math/vec3";
import type { BootMonitor } from "../editor/boot/BootMonitor";
import { EditorModule } from "../editor/EditorModule";
import { RendererModule } from "../render/RendererModule";
import { entityId } from "../world/entity";
import type { EntityId } from "../world/entity";
import { WorldModule } from "../world/WorldModule";
import type { DebugFacade, ScreenPos } from "./debugFacadeTypes";
import type { ThumbnailImage } from "../render/thumbnailPixels";

/** WebP quality for asset thumbnails: small files, no visible loss at tile size. */
const THUMBNAIL_QUALITY = 0.85;

export function isDebugEnabled(win: Window): boolean {
  return import.meta.env.DEV || new URLSearchParams(win.location.search).has("debug");
}

export function installDebugFacade(win: Window, facade: DebugFacade): void {
  if (isDebugEnabled(win)) win.__kn = facade;
}

/** Reads the engine lazily, so the facade works during boot and returns nulls until modules exist. */
export function debugFacadeFor(engine: Engine, monitor: BootMonitor): DebugFacade {
  const bus = () => (engine.has(CommandsModule) ? engine.get(CommandsModule).bus : null);
  return {
    phase: () => monitor.getSnapshot().phase,
    renderApi: () =>
      engine.has(RendererModule) ? engine.get(RendererModule).backend.info.api : null,
    log: () =>
      engine.log
        .getSnapshot()
        .entries.map(({ category, verbosity, message }) => ({ category, verbosity, message })),
    journal: () =>
      bus()
        ?.journal.getSnapshot()
        .entries.map((entry) => ({ ...entry.request })) ?? [],
    undoDepth: () => bus()?.undoState.getSnapshot().undoDepth ?? 0,
    selection: () =>
      engine.has(EditorModule) ? engine.get(EditorModule).selection.getSnapshot() : null,
    transform: (id) =>
      engine.has(WorldModule)
        ? (engine.get(WorldModule).world.component(entityId(id), "Transform") ?? null)
        : null,
    entityScreenPos: (id) =>
      engine.has(EditorModule) ? entityScreenPos(engine, entityId(id)) : null,
    gizmoPosition: () => {
      if (!engine.has(EditorModule)) return null;
      const pose = engine.get(EditorModule).gizmo.pose;
      return pose ? mat4GetTranslation(pose) : null;
    },
    placementPreview: () => {
      const preview = engine.has(EditorModule) ? engine.get(EditorModule).placement.current : null;
      return preview && { asset: preview.asset, position: mat4GetTranslation(preview.world) };
    },
    worldPosition: (id) => {
      if (!engine.has(EditorModule) || !engine.get(WorldModule).world.get(entityId(id)))
        return null;
      return mat4GetTranslation(engine.get(EditorModule).bridge.worldMatrixOf(entityId(id)));
    },
    frameCount: () => (engine.has(CoreModule) ? engine.get(CoreModule).frameLoop.framesRun : 0),
    modelsLoading: () =>
      engine.has(RendererModule) ? engine.get(RendererModule).backend.modelsLoading() : 0,
    camera: () => {
      if (!engine.has(EditorModule)) return null;
      const { position, yaw, pitch } = engine.get(EditorModule).camera.getSnapshot();
      return { position, yaw, pitch };
    },
    project: (point) =>
      engine.has(EditorModule) ? toPage(engine, [point[0], point[1], point[2]]) : null,
    readPixel: async (x, y) => {
      if (!engine.has(RendererModule)) return [0, 0, 0, 0];
      // What the editor describes now: the renderer takes the camera and world
      // changes in a frame's bridge phase, while project() reads the editor's
      // camera at once. Reading before that frame would sample the old view.
      await nextFrame(engine.get(CoreModule).frameLoop);
      return engine.get(RendererModule).backend.readPixel(x, y);
    },
    assetThumbnail: async (path, width, height) => {
      if (!engine.has(RendererModule)) return null;
      const image = await engine.get(RendererModule).backend.renderThumbnail(path, width, height);
      return image && encodeWebp(image);
    },
  };
}

/** A thumbnail as a WebP data URL, through a 2D canvas (which keeps its alpha). */
function encodeWebp(image: ThumbnailImage): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.putImageData(new ImageData(new Uint8ClampedArray(image.data), image.width), 0, 0);
  return canvas.toDataURL("image/webp", THUMBNAIL_QUALITY);
}

function entityScreenPos(engine: Engine, id: EntityId): ScreenPos | null {
  const { bridge } = engine.get(EditorModule);
  if (!engine.get(WorldModule).world.get(id)) return null;

  const bounds = bridge.boundsOf([id]);
  return bounds && toPage(engine, aabbCenter(bounds));
}

/** A world point in page coordinates, if it's in front of the camera and inside the viewport. */
function toPage(engine: Engine, point: Vec3): ScreenPos | null {
  const viewport = document.querySelector(".kn-viewport-panel")?.getBoundingClientRect();
  if (!viewport) return null;

  const screen = engine.get(EditorModule).camera.project(point);
  const inside =
    screen.x >= 0 && screen.y >= 0 && screen.x <= viewport.width && screen.y <= viewport.height;
  return screen.visible && inside
    ? { x: viewport.left + screen.x, y: viewport.top + screen.y }
    : null;
}
